import ELK, { type ElkNode } from 'elkjs/lib/elk.bundled.js';

import { getRelationshipLabelSize } from '@/components/diagrams/FlowChart/nodes/RelationshipLabelNode/relationshipLabelSize';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  ELK_LABEL_LAYOUT_OPTIONS,
  ELK_LAYOUT_OPTIONS,
  ELK_NODE_LAYOUT_OPTIONS,
  STAGE_GAP,
} from './diagramLayout.config';
import {
  createReactFlowDiagram,
  type ReactFlowDiagram,
} from './reactFlowDiagram';

const elk = new ELK();

export type DiagramSize = { width: number; height: number };
/** The tiles and arrows at their ELK positions, with the ELK routes. */
export type ReactFlowDiagramLayout = ReactFlowDiagram & {
  bounds?: { x: number; y: number; width: number; height: number };
};

/**
 * Returns the size of each tile in `reactFlowDiagram` for ELK, or null until
 * React Flow measures every tile. React Flow reports the sizes through
 * onNodesChange, and the node state `nodes` keeps them. A tile with a set
 * width renders at that width, so ELK uses the set width at once, before
 * React Flow measures the tile again.
 */
export function getTileSizes(
  reactFlowDiagram: ReactFlowDiagram,
  nodes: ReactFlowDiagram['nodes']
): Record<string, DiagramSize> | null {
  const measured = new Map(nodes.map((node) => [node.id, node.measured]));
  const sizes: Record<string, DiagramSize> = {};
  for (const tile of reactFlowDiagram.nodes) {
    const size = measured.get(tile.id);
    if (!size?.width || !size.height) return null;
    sizes[tile.id] = {
      width: tile.width ?? size.width,
      height: size.height,
    };
  }
  return sizes;
}

/**
 * Returns a string that changes only when the automatic layout must change:
 * when the tiles, connections, label sizes, tile widths, or stage order
 * change. Other text edits do not change it, so tiles and routes keep their
 * positions until the user selects "Auto tiles".
 */
export function getLayoutKey(
  diagram: DiagramBuilder_DiagramFragment,
  reactFlowDiagram: ReactFlowDiagram
) {
  return JSON.stringify([
    // Stage labels show on the bands. They do not change the layout.
    diagram.content.stages.map((stage) => stage.id),
    reactFlowDiagram.nodes.map((node) => [
      node.id,
      node.width ?? null,
      node.data.item.stageId ?? null,
    ]),
    reactFlowDiagram.edges.map((edge) => [
      edge.id,
      edge.source,
      edge.target,
      // A label edit that changes the number of lines changes the label box.
      getEdgeLabelSize(edge.data!.label)?.height ?? null,
    ]),
  ]);
}

/**
 * Returns the box that ELK reserves for a connection label, or null when the
 * connection has no label. The canvas draws each label at the shared
 * relationship label width, so a long label wraps. React Flow does not measure
 * edge labels, so the height is an estimate of the wrapped text.
 */
export function getEdgeLabelSize(label: string): DiagramSize | null {
  return label ? getRelationshipLabelSize(label) : null;
}

/** Returns true when any tile has a saved position. */
export function hasSavedPositions(diagram: DiagramBuilder_DiagramFragment) {
  return diagram.content.nodes.some((node) => node.position);
}

/** Return rendered dimensions, or throw when they are missing or invalid. */
function measured(sizes: Record<string, DiagramSize>, id: string): DiagramSize {
  const size = sizes[id];
  if (
    !size ||
    !Number.isFinite(size.width) ||
    !Number.isFinite(size.height) ||
    size.width <= 0 ||
    size.height <= 0
  ) {
    throw new Error(`Missing rendered dimensions for ${id}`);
  }
  return size;
}

/**
 * Lay out tiles at the sizes that React Flow measured, keyed by tile ID, in
 * declared stage order when stages exist. Connection labels take
 * getEdgeLabelSize. Preserve saved positions and omit automatic routes when
 * positions override ELK.
 */
export async function layoutDiagram(
  diagram: DiagramBuilder_DiagramFragment,
  tileSizes: Record<string, DiagramSize>
): Promise<ReactFlowDiagramLayout> {
  if (diagram.content.stages.length) return layoutStages(diagram, tileSizes);
  return layoutGraph(diagram, tileSizes);
}

/**
 * Use ELK to place one graph and route its edges around measured tiles and labels.
 * Within a stage, disconnected nodes share layers instead of separate packed components.
 */
async function layoutGraph(
  diagram: DiagramBuilder_DiagramFragment,
  tileSizes: Record<string, DiagramSize>,
  withinStage = false
): Promise<ReactFlowDiagramLayout> {
  const reactFlowDiagram = createReactFlowDiagram(diagram);
  if (!reactFlowDiagram.nodes.length) return reactFlowDiagram;
  const graph: ElkNode = {
    id: 'diagram-layout',
    layoutOptions: {
      ...ELK_LAYOUT_OPTIONS,
      // Disconnected recipients in one stage share a layer, not a packed column.
      'elk.separateConnectedComponents': String(!withinStage),
      'elk.direction': 'DOWN',
    },
    children: [...reactFlowDiagram.nodes]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((node) => {
        const { width, height } = measured(tileSizes, node.id);
        return {
          id: node.id,
          width,
          height,
          layoutOptions: ELK_NODE_LAYOUT_OPTIONS,
          ports: [
            {
              id: `${node.id}/in`,
              x: width / 2,
              y: 0,
              width: 0,
              height: 0,
              layoutOptions: { 'elk.port.side': 'NORTH' },
            },
            {
              id: `${node.id}/out`,
              x: width / 2,
              y: height,
              width: 0,
              height: 0,
              layoutOptions: { 'elk.port.side': 'SOUTH' },
            },
          ],
        };
      }),
    edges: [...reactFlowDiagram.edges]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((edge) => {
        const labelSize = getEdgeLabelSize(edge.data!.label);
        return {
          id: edge.id,
          sources: [`${edge.source}/out`],
          targets: [`${edge.target}/in`],
          labels: labelSize
            ? [
                {
                  id: `${edge.id}/label`,
                  text: edge.data!.label,
                  ...labelSize,
                  layoutOptions: ELK_LABEL_LAYOUT_OPTIONS,
                },
              ]
            : [],
        };
      }),
  };
  const solved = await elk.layout(graph);
  const placedNodes = new Map(solved.children?.map((node) => [node.id, node]));
  const placedEdges = new Map(solved.edges?.map((edge) => [edge.id, edge]));
  const manual = hasSavedPositions(diagram);
  return {
    bounds: manual
      ? undefined
      : { x: 0, y: 0, width: solved.width!, height: solved.height! },
    nodes: reactFlowDiagram.nodes.map((node) => {
      const placed = placedNodes.get(node.id)!;
      return {
        ...node,
        position: node.data.item.position ?? { x: placed.x!, y: placed.y! },
      };
    }),
    edges: reactFlowDiagram.edges.map((edge) => {
      // Static routes are valid only for the positions ELK solved. Never apply
      // them to a manually moved graph and imply that it was obstacle-routed.
      if (manual) return edge;
      const placed = placedEdges.get(edge.id)!;
      const section = placed.sections?.[0];
      const label = placed.labels?.[0];
      if (
        !section ||
        (edge.data!.label && (label?.x === undefined || label.y === undefined))
      )
        throw new Error(`Incomplete layout for ${edge.id}`);
      return {
        ...edge,
        data: {
          ...edge.data!,
          routeWaypoints: section.bendPoints ?? [],
          labelPosition: label
            ? {
                x: label.x! + label.width! / 2,
                y: label.y! + label.height! / 2,
              }
            : undefined,
        },
      };
    }),
  };
}

/**
 * Lay out each stage separately, then place stages from top to bottom in
 * declared order. Reserve space for empty stages and labels, and retain saved positions.
 */
async function layoutStages(
  diagram: DiagramBuilder_DiagramFragment,
  tileSizes: Record<string, DiagramSize>
): Promise<ReactFlowDiagramLayout> {
  const reactFlowDiagram = createReactFlowDiagram(diagram);
  const nodes: ReactFlowDiagramLayout['nodes'] = [];
  const positionsByID = new Map(
    diagram.content.nodes.flatMap((node) =>
      node.position ? [[node.nodeID, node.position] as const] : []
    )
  );
  const edges = new Map<string, ReactFlowDiagramLayout['edges'][number]>();
  // Cross-stage labels sit in the gap between stages, so the gap holds the
  // tallest label.
  const labelGap =
    STAGE_GAP +
    Math.max(
      0,
      ...reactFlowDiagram.edges.map(
        (edge) => getEdgeLabelSize(edge.data!.label)?.height ?? 0
      )
    );
  const layouts = await Promise.all(
    diagram.content.stages.map(async (stage) => {
      // Each stage is laid out without saved positions. The saved positions
      // replace the stage positions after the stages are placed.
      const members = diagram.content.nodes
        .filter((node) => node.stageId === stage.id)
        .map((node) => ({ ...node, position: null }));
      const ids = new Set(members.map((node) => node.nodeID));
      return layoutGraph(
        {
          ...diagram,
          content: {
            ...diagram.content,
            stages: [],
            nodes: members,
            edges: diagram.content.edges.filter(
              (edge) => ids.has(edge.sourceNodeID) && ids.has(edge.targetNodeID)
            ),
          },
        },
        tileSizes,
        true
      );
    })
  );
  const diagramWidth = Math.max(
    STAGE_GAP,
    ...layouts.map((layout) => layout.bounds?.width ?? 0)
  );
  let cursor = labelGap;
  for (const layout of layouts) {
    // Center each stage across the widest stage.
    const offset = {
      x: STAGE_GAP + (diagramWidth - (layout.bounds?.width ?? 0)) / 2,
      y: cursor,
    };
    for (const node of layout.nodes)
      nodes.push({
        ...node,
        position: positionsByID.get(node.id) ?? {
          x: node.position.x + offset.x,
          y: node.position.y + offset.y,
        },
      });
    for (const edge of layout.edges) {
      const data = edge.data!;
      edges.set(edge.id, {
        ...edge,
        data: {
          ...data,
          routeWaypoints: data.routeWaypoints?.map((p) => ({
            x: p.x + offset.x,
            y: p.y + offset.y,
          })),
          labelPosition: data.labelPosition
            ? {
                x: data.labelPosition.x + offset.x,
                y: data.labelPosition.y + offset.y,
              }
            : undefined,
        },
      });
    }
    cursor += layout.bounds?.height ?? STAGE_GAP;
    cursor += labelGap;
  }
  // Cross-stage edges have no ELK route. ArrowEdge draws them from the current
  // source and target handles, without obstacle avoidance. Saved node positions
  // also discard ELK routes because those routes describe the automatic positions.
  return {
    nodes,
    edges: reactFlowDiagram.edges.map((edge) =>
      hasSavedPositions(diagram) ? edge : (edges.get(edge.id) ?? edge)
    ),
  };
}
